import { clsx } from "clsx";
import { useCallback, useEffect, useId, useRef, useState } from "react";

export interface FacetOption<T extends string> {
  token: T;
  label: string;
  /** Optional colored glyph shown before the label (race sigils). */
  sigil?: { glyph: string; className: string };
  title?: string;
}

// The one dropdown look both variants share (multi-select facets on the roster, the
// single-select faction picker on /tech): trigger, floating panel, and option rows are
// the same strings so the two cannot drift apart.
const TRIGGER_CLASS =
  "flex cursor-pointer select-none items-baseline gap-1.5 rounded border px-2 py-1 font-mono text-[11.5px] uppercase tracking-[0.08em] transition-colors";

/** A trigger's tone: lit while the control is holding something, quiet while it is not. The
 * Tech dropdown wears the same one, so the toolbar reads as one row of controls. */
export function triggerTone(active: boolean): string {
  return active
    ? "border-line-strong bg-raised text-ink"
    : "border-line text-ink-3 hover:border-line-strong hover:text-ink-2";
}

/** The explicit ring the four stat-frame triggers wear: the shell otherwise leaves a trigger
 * on the browser's default outline, which is thinner and a different colour than the rest of
 * that toolbar. Never `outline-none` — it sets --tw-outline-style: none and defeats
 * focus-visible outright. The roster's facet dropdowns and the Faction Picker deliberately do
 * NOT wear it; they are on the default outline and this change did not move them. */
export const TRIGGER_FOCUS_RING =
  "focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-link";
const PANEL_CLASS =
  "absolute top-full z-20 mt-1 min-w-44 rounded-lg border border-line-strong bg-surface py-1 shadow-[0_8px_24px_rgba(0,0,0,0.5)]";
const OPTION_CLASS =
  "flex w-full cursor-pointer items-baseline gap-2 px-3 py-1.5 text-left text-[13px] hover:bg-raised";

/** One option row: a tick box, an optional race sigil, and the label. `indent` steps a row
 * in under the one above it — the Faction Picker's two-level shape, whose pages are Races
 * and whose entries are that Race's Factions (ADR 0010). */
export function OptionRow<T extends string>({
  option,
  checked,
  partial = false,
  indent = false,
  onClick,
}: {
  option: FacetOption<T>;
  checked: boolean;
  /** Some but not all of a group's entries are ticked — the Race row's third state. */
  partial?: boolean;
  indent?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-pressed={checked}
      title={option.title}
      onClick={onClick}
      className={clsx(OPTION_CLASS, indent && "pl-7")}
    >
      <span
        aria-hidden
        className={clsx(
          "inline-flex h-3.5 w-3.5 shrink-0 translate-y-px items-center justify-center rounded-sm border text-[9px] leading-none",
          checked || partial ? "border-ink-2 text-ink" : "border-line-strong text-transparent",
        )}
      >
        {partial && !checked ? "–" : "✓"}
      </span>
      {option.sigil && (
        <span aria-hidden className={clsx("text-[9px]", option.sigil.className)}>
          {option.sigil.glyph}
        </span>
      )}
      <span className={checked ? "text-ink" : "text-ink-2"}>{option.label}</span>
    </button>
  );
}

/** Close on outside pointerdown or Escape while open — the shared dismiss contract
 * (also the states legend's, so every popover dismisses the same way). */
export function useDismiss(
  open: boolean,
  rootRef: React.RefObject<HTMLDivElement | null>,
  close: () => void,
) {
  useEffect(() => {
    if (!open) return;
    function onPointerDown(event: PointerEvent) {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) close();
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") close();
    }
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open, rootRef, close]);
}

/** The open/close machinery every dropdown needs: the state, the root ref the dismiss
 * contract measures against, the trigger ref a dismissal hands focus back to, and the id
 * wiring aria-controls to the panel.
 *
 * `controlled` is for a caller that owns the open state because something OUTSIDE the control
 * depends on it — the Loadout control's note, which the page says while the panel is shut and
 * the panel says while it is open, so exactly one of the two says it. */
export function useDropdown(controlled?: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const [ownOpen, setOwnOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listId = useId();
  const open = controlled?.open ?? ownOpen;
  const onOpenChange = controlled?.onOpenChange;
  const setOpen = useCallback(
    (next: boolean) => {
      if (onOpenChange === undefined) setOwnOpen(next);
      else onOpenChange(next);
    },
    [onOpenChange],
  );
  // Dismissing hands focus back to the trigger, so Escape never strands the keyboard in a
  // panel that is no longer there. Before this was shared (review 2026-09-08) only the two
  // hand-rolled controls did it: Tab into a Corruption or Level panel and Escape dropped
  // focus to <body>, measured on the served build.
  const close = useCallback(() => {
    setOpen(false);
    triggerRef.current?.focus();
  }, [setOpen]);
  useDismiss(open, rootRef, close);
  return { open, setOpen, close, rootRef, triggerRef, listId };
}

/** The shell every dropdown renders: a positioned root, the trigger button (whose CONTENT is
 * the caller's — a facet name with a count, the selected option with its sigil, or the
 * Faction Selection's state), and the floating panel holding the caller's rows. Exported so
 * the Faction Picker's two-level panel is the same control, not a lookalike. */
export function DropdownShell({
  dropdown,
  triggerClass,
  trigger,
  label,
  align = "start",
  panel,
  children,
}: {
  dropdown: ReturnType<typeof useDropdown>;
  triggerClass: string;
  trigger: React.ReactNode;
  /** The control's accessible name, for a trigger whose own text is a changing state. */
  label?: string;
  /** Which edge the panel hangs from. "end" for a control near the right edge — a panel
   * wider than its trigger overflows the viewport at phone width otherwise. */
  align?: "start" | "end";
  /** A whole panel of the caller's own, in place of the default list of rows — the Tech and
   * Loadout controls open a PickerPanel, which carries its own header, note and scroller and
   * nudges itself back on screen through its own ref (picker-panel.tsx). It renders inside
   * this shell's positioned root, so its anchoring is unchanged. The caller wires it to
   * `dropdown.listId` and `dropdown.close`, and supplies no `children`. */
  panel?: React.ReactNode;
  children?: React.ReactNode;
}) {
  const { open, setOpen, rootRef, triggerRef, listId } = dropdown;
  return (
    <div ref={rootRef} className="relative">
      <button
        ref={triggerRef}
        type="button"
        aria-expanded={open}
        aria-controls={listId}
        aria-label={label}
        onClick={() => setOpen(!open)}
        className={clsx(TRIGGER_CLASS, triggerClass, open && "border-line-strong text-ink")}
      >
        {trigger}
        <span aria-hidden className="text-[9px] text-muted-2">
          {open ? "▲" : "▼"}
        </span>
      </button>
      {open &&
        (panel ?? (
          <div id={listId} className={clsx(PANEL_CLASS, align === "end" ? "right-0" : "left-0")}>
            {children}
          </div>
        ))}
    </div>
  );
}

/** One facet as a compact dropdown: a quiet toolbar button showing the facet name and how
 * many of its options are active, opening a multi-select checklist. Selection never closes
 * the list (multi-select); outside click and Escape do. */
export function FacetDropdown<T extends string>({
  label,
  options,
  selected,
  onToggle,
}: {
  label: string;
  options: FacetOption<T>[];
  selected: readonly T[];
  onToggle: (token: T) => void;
}) {
  const dropdown = useDropdown();
  const active = selected.length > 0;
  return (
    <DropdownShell
      dropdown={dropdown}
      triggerClass={triggerTone(active)}
      trigger={
        <>
          {label}
          {active && <span className="text-[10.5px] text-ink-2">· {selected.length}</span>}
        </>
      }
    >
      {/* Multi-select: choosing never closes the list. */}
      {options.map((option) => (
        <OptionRow
          key={option.token}
          option={option}
          checked={selected.includes(option.token)}
          onClick={() => onToggle(option.token)}
        />
      ))}
    </DropdownShell>
  );
}
